/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    BoolFieldDefinition,
    ToggleFormGroup,
    ControlType,
    FieldType,
    ISettingGroupDefinition,
    Levels,
    SettingGroupDefinition,
    StringResource,
    UIDefinitions
} from "@nuance/hierarchical-settings-ui-domain";
import {
    IFieldDefinitionPanelToggleData,
    IFieldDefinitionPanelToggleProps,
    IFieldDefinitionsPanelData,
    IFieldDefinitionsPanelProps
} from "../Panels";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { IDomainDataMapper } from "./IDomainDataMapper";

const logger: ILogger = getLogger();

export class DomainDataMapperToggle implements IDomainDataMapper {
    /**
     * Field type expected by this mapper
     */
    readonly RequiredFieldType = FieldType.Boolean;

    /**
     * Control type expected by this mapper
     */
    readonly RequiredControlType = ControlType.ToggleField;

    /**
     * Setting group definition object
     */

    readonly definition: ISettingGroupDefinition;

    constructor(definition: ISettingGroupDefinition) {
        this.definition = definition;
    }

    /**
     * Build panel properties for adding a new control to
     * the definition.
     *
     * @returns Panel definition properties.
     */
    BuildPanelPropsForNewItem = (): IFieldDefinitionsPanelProps => {
        const result: IFieldDefinitionPanelToggleProps = {
            common: {
                panelTitle: "Add Toggle",
                originalSettingName: "",
                isNew: true,
                isOpen: true
            },
            data: {
                definitionType: ControlType.ToggleField,
                settingName: "",
                title: "",
                tooltip: "",
                helper: "",
                helpTextValid: true,
                defaultValue: false,
                accessLevels: Levels.Classic,
                auditChanges: false,
                visibleRule: "",
                enableRule: "",
                includeToggleLabels: true,
                offLabel: "Off",
                onLabel: "On"
            }
        };

        return result;
    };

    /**
     * Build panel properties for an existing control in
     * the setting definition.
     *
     * @param ctrlGuid Control GUID
     * @returns Panel definition properties
     */
    BuildPanelPropsForExistingItem = (ctrlGuid: string): IFieldDefinitionsPanelProps => {
        const sg = new SettingGroupDefinition(this.definition);
        const [formGroup, fieldDef] = this.findControlAndFieldDefinition(sg, ctrlGuid);
        const sr = sg.UI.StringResources;

        const offLabel =
            sr.get(StringResource.CreateMapKey(formGroup.OffLabelStringId, "en-US"))?.Text ?? "";
        const onLabel =
            sr.get(StringResource.CreateMapKey(formGroup.OnLabelStringId, "en-US"))?.Text ?? "";

        const includeToggleLabels = offLabel !== "" && onLabel !== "";

        const result: IFieldDefinitionPanelToggleProps = {
            common: {
                panelTitle: "Edit Toggle",
                originalSettingName: formGroup.SettingName,
                isNew: false,
                isOpen: true
            },
            data: {
                ctrlGuid,
                definitionType: ControlType.ToggleField,
                settingName: fieldDef.SettingName,
                title:
                    sr.get(StringResource.CreateMapKey(formGroup.LabelTextStringId, "en-US"))
                        ?.Text ?? "",
                tooltip:
                    sr.get(StringResource.CreateMapKey(formGroup.TooltipStringId, "en-US"))?.Text ??
                    "",
                helper:
                    sr.get(StringResource.CreateMapKey(formGroup.HelperTextStringId, "en-US"))
                        ?.Text ?? "",
                helpTextValid: true,
                defaultValue: fieldDef.DefaultValue,
                accessLevels: fieldDef.AccessLevel,
                auditChanges: fieldDef.AuditChanges,
                visibleRule: formGroup.VisibleRuleName,
                enableRule: formGroup.EnableRuleName,
                includeToggleLabels,
                offLabel: includeToggleLabels ? offLabel : "Off",
                onLabel: includeToggleLabels ? onLabel : "On"
            }
        };

        return result;
    };

    /**
     * Saves panel data to the setting definition.
     *
     * @param data Panel data
     * @returns New/updated setting group definition
     */
    SaveDataToDefinition = (data: IFieldDefinitionsPanelData): ISettingGroupDefinition => {
        if (data.definitionType !== ControlType.ToggleField) {
            logger.logError("Invalid control type", { type: ControlType[data.definitionType] });
            throw new Error("Invalid definition type.");
        }

        const td = data as IFieldDefinitionPanelToggleData;
        const sg = new SettingGroupDefinition(this.definition);

        if (data.ctrlGuid === undefined) {
            this.addNewToggleField(td, sg);
        } else {
            this.updateExistingToggleField(td, sg);
        }
        return sg.toISettingGroupDefinition();
    };

    /**
     * Adds a new toggle field to the setting definition
     * @param data Panel toggle data
     * @param sg Setting group definition
     */
    private addNewToggleField(
        data: IFieldDefinitionPanelToggleData,
        sg: SettingGroupDefinition
    ): void {
        const bfd = new BoolFieldDefinition(data.settingName, data.defaultValue);
        bfd.AccessLevel = data.accessLevels;
        bfd.AuditChanges = data.auditChanges;
        sg.AddFieldDefinition(bfd);

        const formGroup = sg.UI.AddToggleFormGroup(data.settingName);
        formGroup.EnableRuleName = data.enableRule;
        formGroup.VisibleRuleName = data.visibleRule;

        if (!data.includeToggleLabels) {
            data.offLabel = "";
            data.onLabel = "";
        }

        this.updateStringResources(
            sg.UI,
            formGroup,
            data.title,
            data.tooltip,
            data.helper,
            data.offLabel,
            data.onLabel
        );
    }

    /**
     *
     * @param data Panel toggle data
     * @param sg String group definition
     */
    private updateExistingToggleField(
        data: IFieldDefinitionPanelToggleData,
        sg: SettingGroupDefinition
    ): void {
        if (data.ctrlGuid === undefined) {
            logger.logError("Control guid is undefined.");
            throw new Error("Control guid is undefined.");
        }

        const [formGroup, sfd] = this.findControlAndFieldDefinition(sg, data.ctrlGuid);
        sfd.DefaultValue = data.defaultValue;
        sfd.AccessLevel = data.accessLevels;
        sfd.AuditChanges = data.auditChanges;
        formGroup.EnableRuleName = data.enableRule;
        formGroup.VisibleRuleName = data.visibleRule;

        if (!data.includeToggleLabels) {
            data.offLabel = "";
            data.onLabel = "";
        }

        this.updateStringResources(
            sg.UI,
            formGroup,
            data.title,
            data.tooltip,
            data.helper,
            data.offLabel,
            data.onLabel
        );
    }

    /**
     * Updates the string resources in the definition.
     * @param sg Setting group definition class
     * @param formGroup Toggle form group
     * @param label Label text
     * @param tooltip Tooltip text
     */
    private updateStringResources(
        ui: UIDefinitions,
        formGroup: ToggleFormGroup,
        label: string,
        tooltip: string,
        helper: string,
        offLabel: string,
        onLabel: string
    ) {
        const stringIdValues: [stringId: string, value: string][] = [
            [formGroup.LabelTextStringId, label],
            [formGroup.TooltipStringId, tooltip],
            [formGroup.HelperTextStringId, helper],
            [formGroup.OffLabelStringId, offLabel],
            [formGroup.OnLabelStringId, onLabel]
        ];

        for (const [stringId, value] of stringIdValues) {
            if (!ui.UpdateStringResourceText(stringId, value)) {
                logger.logError("Error finding string resource", { stringId });
                throw new Error("Error finding string resource");
            }
        }
    }

    /**
     * Find the control and field definition for a
     * control GUID
     * @param sg Setting group definition
     * @param ctrlGuid Control GUID
     * @returns ToggleFormGroup and BoolFieldDefinition
     * associated with the control
     */
    private findControlAndFieldDefinition = (
        sg: SettingGroupDefinition,
        ctrlGuid: string
    ): [ToggleFormGroup, BoolFieldDefinition] => {
        const ctrlIndex = sg.UI.Controls.findIndex(i => i.CtrlGuid == ctrlGuid);
        if (ctrlIndex === -1) {
            logger.logError("Unable to find control", { ctrlGuid });
            throw new Error("Unable to find control.");
        }

        const uiCtrl = sg.UI.Controls[ctrlIndex];
        if (uiCtrl.ControlDiscriminator !== this.RequiredControlType) {
            logger.logError("Control type is not toggle", {
                type: ControlType[uiCtrl.ControlDiscriminator]
            });
            throw new Error("Invalid control type.  Expecting Toggle control.");
        }

        const formGroup = uiCtrl as ToggleFormGroup;
        const fd = sg.Fields.get(formGroup.SettingName);
        if (fd === undefined || fd.FieldDiscriminator !== this.RequiredFieldType) {
            logger.logError("Field type is not toggle", {
                type: fd?.FieldDiscriminator.toString()
            });
            throw new Error("Invalid field type.");
        }

        return [formGroup, fd as BoolFieldDefinition];
    };
}
